// Player kinematics, shared verbatim by the server tick and the client's
// prediction so both integrate identically. Pure functions only. Geometry
// comes from the area plan, so one integrator serves every room.

import { type Carriage, carriageSpeedCap } from "./carriage";
import { type Aabb, PLAYER_RADIUS, PLAYER_SPEED, SPRINT_SPEED, WALK_SPEED } from "./constants";
import type { InputMessage } from "./protocol";
import type { AreaPlan } from "./world-plan";

export interface PlayerPose {
  x: number;
  z: number;
  yaw: number;
}

/** Normalize an angle to [-π, π]. */
export function wrapAngle(a: number): number {
  const wrapped = ((((a + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI;
  return wrapped;
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Push a player circle at (x, z) out of a box if it overlaps. Returns the
 * corrected position.
 */
function pushOut(x: number, z: number, box: Aabb, radius: number): { x: number; z: number } {
  const cx = clamp(x, box.minX, box.maxX);
  const cz = clamp(z, box.minZ, box.maxZ);
  const dx = x - cx;
  const dz = z - cz;
  const distSq = dx * dx + dz * dz;
  if (distSq >= radius * radius) return { x, z };

  if (distSq > 1e-12) {
    // Centre is outside the box: push away from the closest point.
    const dist = Math.sqrt(distSq);
    const scale = radius / dist;
    return { x: cx + dx * scale, z: cz + dz * scale };
  }

  // Centre is inside the box: exit through the nearest face.
  const exits = [
    { x: box.minX - radius, z, cost: x - (box.minX - radius) },
    { x: box.maxX + radius, z, cost: box.maxX + radius - x },
    { x, z: box.minZ - radius, cost: z - (box.minZ - radius) },
    { x, z: box.maxZ + radius, cost: box.maxZ + radius - z },
  ];
  let best = exits[0] as (typeof exits)[number];
  for (const e of exits) {
    if (e.cost < best.cost) best = e;
  }
  return { x: best.x, z: best.z };
}

// ---------------------------------------------------------------------------
// The obstacle index.
//
// `plan.obstacles` used to be walked in full, for every player, every
// tick. That was fine while a plan's obstacles were its furniture. Then
// housing started appending a footprint per standing structure on a
// 16.4 km planet — capped at one per (claim, kind) and one claim per
// character, so the set grows linearly and permanently with the
// playerbase, which is unbounded in the only dimension that matters. A
// homestead seven kilometres away was collision-tested against every
// walker in the settlement twenty times a second: measured at 17
// obstacles → 0.11 ms/tick and 2017 obstacles → 2.00 ms/tick with 40
// walkers, an 18× regression against a 50 ms budget and still climbing
// (adversarial review C26). The comment atop wilds/ai.ts is proud that
// the creature tick is O(creatures) via a spatial hash; this is that.
//
// Cached on the obstacle ARRAY rather than on the plan, in a WeakMap:
// PlanetRoom replaces the array whenever the standing set changes (the
// shared plan template must stay pristine), so a new array is a new
// index and there is no invalidation to get wrong. Both sides build it
// from the same function over the same list, so prediction still agrees
// exactly — and it is exact anyway: pushOut is a no-op for a box the
// capsule does not touch, so skipping distant boxes cannot change an
// answer.

/** Grid cell, metres. Comfortably larger than any push a tick can make. */
const OBSTACLE_CELL_M = 32;
/** Below this, the scan is cheaper than the lookup. Rooms stay rooms. */
const OBSTACLE_INDEX_MIN = 24;

type ObstacleIndex = Map<string, number[]>;

const obstacleIndexes = new WeakMap<readonly Aabb[], ObstacleIndex>();

function cellKey(cx: number, cz: number): string {
  return `${cx},${cz}`;
}

function buildObstacleIndex(boxes: readonly Aabb[]): ObstacleIndex {
  const cells: ObstacleIndex = new Map();
  for (let i = 0; i < boxes.length; i++) {
    const box = boxes[i] as Aabb;
    const x0 = Math.floor(box.minX / OBSTACLE_CELL_M);
    const x1 = Math.floor(box.maxX / OBSTACLE_CELL_M);
    const z0 = Math.floor(box.minZ / OBSTACLE_CELL_M);
    const z1 = Math.floor(box.maxZ / OBSTACLE_CELL_M);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const key = cellKey(cx, cz);
        const list = cells.get(key);
        if (list) list.push(i);
        else cells.set(key, [i]);
      }
    }
  }
  return cells;
}

/** Scratch, reused: consumed inside stepPlayer before anything else can
 *  ask for it (pure functions, one thread, no reentrancy). */
const nearbyScratch: number[] = [];

/**
 * Indices of every box within a cell of (x, z), in plan order and without
 * repeats. Plan order matters: pushOut resolves sequentially, so two
 * sides agreeing on the SET is not enough, they have to agree on the
 * sequence.
 */
function nearbyObstacles(index: ObstacleIndex, x: number, z: number): number[] {
  const cx = Math.floor(x / OBSTACLE_CELL_M);
  const cz = Math.floor(z / OBSTACLE_CELL_M);
  nearbyScratch.length = 0;
  for (let ox = -1; ox <= 1; ox++) {
    for (let oz = -1; oz <= 1; oz++) {
      const list = index.get(cellKey(cx + ox, cz + oz));
      if (!list) continue;
      for (const i of list) nearbyScratch.push(i);
    }
  }
  if (nearbyScratch.length > 1) nearbyScratch.sort((a, b) => a - b);
  return nearbyScratch;
}

/**
 * Which speed the player's gait flags earn. Sprint and walk exist only on
 * the surface — clubs keep the tuned indoor pace no matter what the client
 * claims (the server integrates with the same rule, so no speed hacking).
 *
 * `carriage` is the body's own ceiling and applies in every room: what the
 * Edge did to you outdoors does not stop being true when you step through
 * a door. Both sides pass it, so client prediction and the server tick
 * agree and there is nothing to reconcile.
 */
export function gaitSpeed(input: InputMessage, plan: AreaPlan, carriage: Carriage = ""): number {
  const gait =
    plan.kind !== "surface"
      ? PLAYER_SPEED
      : input.sprint === true
        ? SPRINT_SPEED
        : input.walk === true
          ? WALK_SPEED
          : PLAYER_SPEED;
  const cap = carriageSpeedCap(carriage);
  return cap === undefined ? gait : Math.min(gait, cap);
}

/**
 * Advance one player by dt seconds of the given movement intent within an
 * area plan. Input magnitude is clamped to 1 (no speed hacking by sending
 * long vectors), obstacles push the capsule out, and the bounds clamp it in
 * (club walls, or the planet's edge on the surface).
 */
export function stepPlayer(
  pose: PlayerPose,
  input: InputMessage,
  dt: number,
  plan: AreaPlan,
  carriage: Carriage = "",
): PlayerPose {
  let { moveX, moveZ } = input;
  if (!Number.isFinite(moveX) || !Number.isFinite(moveZ)) {
    moveX = 0;
    moveZ = 0;
  }
  const magSq = moveX * moveX + moveZ * moveZ;
  if (magSq > 1) {
    const mag = Math.sqrt(magSq);
    moveX /= mag;
    moveZ /= mag;
  }

  const speed = gaitSpeed(input, plan, carriage);
  let x = pose.x + moveX * speed * dt;
  let z = pose.z + moveZ * speed * dt;

  const boxes = plan.obstacles;
  if (boxes.length < OBSTACLE_INDEX_MIN) {
    for (const box of boxes) {
      ({ x, z } = pushOut(x, z, box, PLAYER_RADIUS));
    }
  } else {
    let index = obstacleIndexes.get(boxes);
    if (!index) {
      index = buildObstacleIndex(boxes);
      obstacleIndexes.set(boxes, index);
    }
    const near = nearbyObstacles(index, x, z);
    let previous = -1;
    for (const i of near) {
      if (i === previous) continue; // a box spanning cells lands twice
      previous = i;
      ({ x, z } = pushOut(x, z, boxes[i] as Aabb, PLAYER_RADIUS));
    }
  }

  const maxX = plan.halfWidth - PLAYER_RADIUS;
  const maxZ = plan.halfDepth - PLAYER_RADIUS;
  x = clamp(x, -maxX, maxX);
  z = clamp(z, -maxZ, maxZ);

  const yaw = Number.isFinite(input.yaw) ? wrapAngle(input.yaw) : pose.yaw;
  return { x, z, yaw };
}
